import type { ReactNode } from 'react';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import type { ControllerRenderProps } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { theme } from '@theguarantors/tg-package-material-ui';

import GroupRadioButtonV2, {
  GroupRadioButtonV2Skeleton,
  type RadioInputBox,
} from '@/components/input/GroupRadioButtonV2';
import { createDataTestId } from '@/utils/createDataTestId';

import type { OptionBoxOptionsSource } from '../../lease-details.config';
import type { OptionBoxOption } from '../../lease-details.types';
import { useResolvedOptions } from '../../hooks';

export type LeaseOptionBoxSelection = {
  /** The selected option's `value` (what lands in form state). */
  value: string;
  /** The selected option's `id` (distinguishes options that share a value). */
  optionId: string;
};

export type LeaseOptionBoxInputProps = {
  name: string;
  options: OptionBoxOptionsSource;
  /** Optional heading above the radios (e.g. "Rent Coverage"). */
  label?: ReactNode;
  /** Second form key that receives the selected option's `id`, if any. */
  companionIdField?: string;
  layout?: 'inline' | 'stacked';
  useDrawerStyles?: boolean;
  skeletonCount?: number;
  required?: boolean;
  disabled?: boolean;
  onSelect?: (selection: LeaseOptionBoxSelection) => void;
};

const toRadioInputBox = (option: OptionBoxOption): RadioInputBox => ({
  id: option.id,
  value: option.value,
  text: option.label,
  subText: option.subText,
  tooltip: option.tooltip,
  disabled: option.disabled,
  disabledTooltip: option.disabledTooltip,
});

// Card-style radio group bound to RHF via `useFormContext`. Mirrors how the
// legacy invite/CLD radios wired `GroupRadioButtonV2` inside a `Controller`, so
// the rendered output is identical — only the binding moves to the engine.
export const LeaseOptionBoxInput = ({
  name,
  options: optionsSource,
  label,
  companionIdField,
  layout,
  useDrawerStyles,
  skeletonCount,
  required = false,
  disabled = false,
  onSelect,
}: LeaseOptionBoxInputProps) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { options, isLoading, isError } = useResolvedOptions<OptionBoxOption>(optionsSource);

  const value = (watch(name) as string | undefined) ?? '';
  const selectedId = companionIdField ? (watch(companionIdField) as string | undefined) : undefined;
  const error = getFieldState(name, formState).error;

  const dataTestId = createDataTestId({ inputString: name, suffix: 'option-box' });

  if (isLoading) {
    // `options` is `[]` during a hook-source's initial load, so `options.length`
    // is `0` and `??` would keep that 0 (it only short-circuits on null/undef),
    // rendering zero skeletons. Use `||` here so the 3-row default is reached.
    return (
      <GroupRadioButtonV2Skeleton
        count={skeletonCount ?? (options.length || 3)}
        dataTestId={`${name}-skeleton`}
      />
    );
  }

  if (isError) {
    return <FormHelperText error>Failed to load options. Please try again.</FormHelperText>;
  }

  const field = {
    name,
    value,
    ref: () => {},
    onBlur: () => trigger(name),
    onChange: (_event: unknown, selectedValue: string, selectedOptionId: string) => {
      setValue(name, selectedValue, { shouldValidate: true, shouldDirty: true });
      if (companionIdField) {
        setValue(companionIdField, selectedOptionId, { shouldDirty: true });
      }
      trigger(name);
      onSelect?.({ value: selectedValue, optionId: selectedOptionId });
    },
  } as unknown as ControllerRenderProps;

  return (
    <Stack direction="column" spacing={0.5}>
      {label != null && label !== '' && (
        <Typography variant="body2" sx={{ mb: '4px' }}>
          {label}
          {required && (
            <Box component="span" sx={{ color: theme.palette.red[600] }}>
              {' '}
              *
            </Box>
          )}
        </Typography>
      )}
      <GroupRadioButtonV2
        id={name}
        dataTestId={dataTestId}
        options={options.map(toRadioInputBox)}
        selectedId={selectedId}
        field={field}
        required={required}
        disabled={disabled}
        layout={layout}
        useDrawerStyles={useDrawerStyles}
      />
      {error?.message && <FormHelperText error>{error.message.toString()}</FormHelperText>}
    </Stack>
  );
};
